import { modApi, contactoStatusApi } from "./api";

export interface Contacto {
  id: string;
  slug: string;
  name: string;
  position?: string;
  phone?: string;
  email?: string;
  client_company_id?: string;
  contact_status_id?: string;
  notes?: string;
  attachments?: any[];
  communication_preferences?: string[];
  last_activity_at?: string;
  created_at?: string;
  updated_at?: string;
  client_company?: {
    id: string;
    name: string;
  };
  contact_status?: {
    id: string;
    name: string;
  };
}

export interface ContactoStatus {
  id: string;
  slug: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClientCompany {
  id: string;
  slug: string;
  name: string;
}

export const contactoAPI = {
  /**
   * Obtiene la lista de contactos (CORE API)
   */
  getContactos: async (companySlug: string, params?: Record<string, any>): Promise<Contacto[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append("slug", companySlug);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const response = await modApi.get(`/contacts?${queryParams.toString()}`);
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Obtiene los estados de contacto (UTILS API)
   */
  getContactoStatuses: async (
    companySlug: string,
  ): Promise<ContactoStatus[]> => {
    const response = await contactoStatusApi.get(
      `/contact-statuses?slug=${companySlug}&is_active=1`,
    );
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  createContactoStatus: async (
    companySlug: string,
    data: { name: string; is_default?: boolean },
  ): Promise<ContactoStatus> => {
    const response = await contactoStatusApi.post(`/contact-statuses`, {
      slug: companySlug,
      name: data.name,
      is_default: data.is_default || false,
    });
    return response.data;
  },

  updateContactoStatus: async (
    companySlug: string,
    statusId: string,
    data: { name: string; is_default?: boolean },
  ): Promise<ContactoStatus> => {
    const response = await contactoStatusApi.put(
      `/utils/contact-statuses/${statusId}`,
      {
        slug: companySlug,
        name: data.name,
        is_default: data.is_default || false,
      },
    );
    return response.data;
  },

  deactivateContactoStatus: async (
    companySlug: string,
    statusId: string,
  ): Promise<ContactoStatus> => {
    const response = await contactoStatusApi.put(
      `/utils/contact-statuses/${statusId}`,
      {
        slug: companySlug,
        is_active: false,
      },
    );
    return response.data;
  },

  getClientCompanies: async (companySlug: string): Promise<ClientCompany[]> => {
    const response = await modApi.get(`/client-companies?slug=${companySlug}`);
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  createContacto: async (
    companySlug: string,
    data: Partial<Contacto>,
  ): Promise<Contacto> => {
    const response = await modApi.post(`/contacts`, {
      ...data,
      slug: companySlug,
    });
    return response.data;
  },

  updateContacto: async (
    companySlug: string,
    contactId: string,
    data: Partial<Contacto>,
  ): Promise<Contacto> => {
    const response = await modApi.put(`/contacts/${contactId}`, {
      ...data,
      slug: companySlug,
    });
    return response.data;
  },

  deleteContacto: async (
    companySlug: string,
    contactId: string,
  ): Promise<void> => {
    await modApi.delete(`/contacts/${contactId}?slug=${companySlug}`);
  },
};
